import { QuizBase } from '../../redux/quiz/types'

// Slider values: '4' = Not at all (best), '1' = Extremely (worst)
// Sum 14 values, raw range = 14-56, normalised to 0-100.
export const SYMPTOM_KEYS = [
  'digestive_symptoms',
  'irregular_bowel_movements',
  'hunger',
  'cravings',
  'joint_and_muscular_discomfort',
  'decreased_physical_strength_or_stamina',
  'declining_skin_quality',
  'declining_hair_quality',
  'irritability',
  'low_mood_or_mood_swings',
  'feeling_stressed_or_on_edge',
  'sleep_problems',
  'tiredness_and_fatigue',
  'brain_fog',
] as const

export type SymptomKey = (typeof SYMPTOM_KEYS)[number]

// Display labels for top-symptom list. Falls back to LABELS for slider questions if not overridden here.
const TOP_SYMPTOM_DISPLAY_LABELS: Partial<Record<SymptomKey, string>> = {
  digestive_symptoms: 'Digestive symptoms',
}

export const SYMPTOM_QUESTION_LABELS: Record<SymptomKey, string> = {
  digestive_symptoms: 'Digestive symptoms (e.g. bloating, gas, discomfort)',
  irregular_bowel_movements: 'Irregular bowel movements',
  hunger: 'Hunger',
  cravings: 'Cravings',
  joint_and_muscular_discomfort: 'Joint and muscular discomfort',
  decreased_physical_strength_or_stamina: 'Decreased physical strength or stamina',
  declining_skin_quality: 'Declining skin quality',
  declining_hair_quality: 'Declining hair quality',
  irritability: 'Irritability',
  low_mood_or_mood_swings: 'Low mood or mood swings',
  feeling_stressed_or_on_edge: 'Feeling stressed or on edge',
  sleep_problems: 'Sleep problems',
  tiredness_and_fatigue: 'Tiredness and fatigue',
  brain_fog: 'Brain fog',
}

// Ascending priority used to break ties when picking the top 3 (index 0 = highest priority).
const TOP_SYMPTOM_PRIORITY: SymptomKey[] = [
  'tiredness_and_fatigue',
  'sleep_problems',
  'hunger',
  'low_mood_or_mood_swings',
  'digestive_symptoms',
  'cravings',
  'feeling_stressed_or_on_edge',
  'decreased_physical_strength_or_stamina',
  'brain_fog',
  'irregular_bowel_movements',
  'irritability',
  'joint_and_muscular_discomfort',
  'declining_skin_quality',
  'declining_hair_quality',
]

const parseAnswer = (raw: string | undefined): number | null => {
  if (!raw) return null
  const n = parseInt(raw, 10)
  if (isNaN(n)) return null
  return n
}

export const getTopSymptomDisplayLabel = (key: SymptomKey): string =>
  TOP_SYMPTOM_DISPLAY_LABELS[key] ?? SYMPTOM_QUESTION_LABELS[key]

export const calculateSymptomScore = (quiz: QuizBase): number => {
  const values = SYMPTOM_KEYS.map((k) => parseAnswer(quiz[k])).filter((v): v is number => v !== null)
  if (values.length === 0) return 0
  // Sum of 14 answers where each is 1-4, raw range 14-56. Backfill missing with 4 (best case).
  const raw = SYMPTOM_KEYS.reduce((acc, k) => acc + (parseAnswer(quiz[k]) ?? 4), 0)
  const normalised = ((raw - 14) / (56 - 14)) * 100
  return Math.round(normalised)
}

export type SymptomScoreBucket = '0-24' | '25-49' | '50-74' | '75-100'

export const getSymptomScoreBucket = (score: number): SymptomScoreBucket => {
  if (score <= 24) return '0-24'
  if (score <= 49) return '25-49'
  if (score <= 74) return '50-74'
  return '75-100'
}

export type FutureScoreBucket = '0-89' | '90-97' | '99-100'

export const getFutureScoreBucket = (score: number): FutureScoreBucket => {
  if (score <= 89) return '0-89'
  if (score <= 97) return '90-97'
  return '99-100'
}

// 20% gap-closure toward 100 — gives the Figma sample values (30 → 44, 90 → 92).
export const calculateImprovedScore = (score: number): number => Math.min(100, score + Math.round((100 - score) * 0.2))

export type SymptomSeverity = 'High' | 'Average' | 'Low'

export const getSymptomSeverity = (rawAnswer: string | undefined): SymptomSeverity => {
  const n = parseAnswer(rawAnswer)
  if (n === null) return 'Low'
  if (n <= 2) return 'High' // 1 = Extremely, 2 = Quite a bit
  if (n === 3) return 'Average'
  return 'Low' // 4 = Not at all
}

export type TopSymptom = {
  key: SymptomKey
  label: string
  severity: SymptomSeverity
}

export const getTopSymptoms = (quiz: QuizBase): TopSymptom[] => {
  const scored = SYMPTOM_KEYS.map((key) => ({
    key,
    answer: parseAnswer(quiz[key]),
  })).filter((s): s is { key: SymptomKey; answer: number } => s.answer !== null)

  scored.sort((a, b) => {
    if (a.answer !== b.answer) return a.answer - b.answer
    return TOP_SYMPTOM_PRIORITY.indexOf(a.key) - TOP_SYMPTOM_PRIORITY.indexOf(b.key)
  })

  return scored.slice(0, 3).map(({ key }) => ({
    key,
    label: getTopSymptomDisplayLabel(key),
    severity: getSymptomSeverity(quiz[key]),
  }))
}
